import { useEffect, useRef, useState } from "react";
import { useLocation } from "wouter";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useAuth, primeAuth } from "@/lib/auth";
import { CheckCircle2, Loader2 } from "lucide-react";

export default function BillingSuccessPage() {
  const [, setLocation] = useLocation();
  const { organization } = useAuth();
  const status = (organization as any)?.subscriptionStatus;
  const [secondsLeft, setSecondsLeft] = useState(60);
  const busy = useRef(false);
  // Prevents double-navigation when the status effect fires more than once
  // (e.g. when the interval re-invalidates /api/auth/me and the status value
  // is re-read with the same "active" value).
  const navigated = useRef(false);

  const sessionId =
    typeof window !== "undefined"
      ? new URLSearchParams(window.location.search).get("session_id")
      : null;

  useEffect(() => {
    let cancelled = false;

    async function confirmAndRefresh() {
      // Ask the backend to verify the checkout session directly with Stripe
      // and activate the workspace. This is the reliable path — it does not
      // depend on a Stripe webhook reaching this environment.
      if (sessionId && !busy.current) {
        busy.current = true;
        try {
          await apiRequest("POST", "/api/billing/confirm", { sessionId });
        } catch {
          // Ignore; the next tick retries (webhook may still be in flight).
        } finally {
          busy.current = false;
        }
      }
      if (!cancelled) {
        queryClient.invalidateQueries({ queryKey: ["/api/auth/me"] });
      }
    }

    confirmAndRefresh();
    const tick = setInterval(() => {
      confirmAndRefresh();
      setSecondsLeft((s) => Math.max(0, s - 3));
    }, 3000);
    return () => {
      cancelled = true;
      clearInterval(tick);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId]);

  useEffect(() => {
    if ((status === "active" || status === "trialing") && !navigated.current) {
      navigated.current = true;
      // Re-prime auth so the plan features, subscription status, and org
      // details in the query cache are fully up-to-date before we enter the
      // app. Then evict the chatbots cache so StaffPortal never reads a
      // pre-provisioning empty result — it will always trigger a fresh fetch
      // after this navigation. No setTimeout: navigate immediately after the
      // network round-trip so there is no arbitrary delay and no race window.
      // Invalidate compliance catalog so the Compliance Center page reflects
      // the new plan's framework entitlements without requiring a browser reload.
      // These are invalidated regardless of primeAuth success/failure because
      // the backend plan-feature cache is already busted by BillingController.
      const invalidateCompliance = () => {
        queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/catalog"] });
        queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance/catalog/summary"] });
        queryClient.invalidateQueries({ queryKey: ["/api/organization/compliance"] });
      };

      primeAuth()
        .then(() => {
          invalidateCompliance();
          queryClient.removeQueries({ queryKey: ["/api/me/chatbots"] });
          setLocation("/");
        })
        .catch(() => {
          // Auth refetch failed (e.g. network hiccup) — navigate anyway so
          // the user is not stuck on the success page. The app shell will
          // refetch auth on mount.
          invalidateCompliance();
          queryClient.removeQueries({ queryKey: ["/api/me/chatbots"] });
          setLocation("/");
        });
    }
  }, [status, setLocation]);

  const isActive = status === "active" || status === "trialing";

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950 p-6 text-white">
      <Card className="w-full max-w-md bg-white/5 border-white/10 text-white">
        <CardContent className="py-10 text-center space-y-5">
          {isActive ? (
            <>
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto" />
              <h1 className="text-2xl font-bold">You're all set!</h1>
              <p className="text-white/60 text-sm">Redirecting you to your workspace…</p>
            </>
          ) : (
            <>
              <Loader2 className="w-14 h-14 text-sky-400 mx-auto animate-spin" />
              <h1 className="text-2xl font-bold">Confirming your payment…</h1>
              <p className="text-white/60 text-sm">
                We're verifying your payment with Stripe and unlocking your workspace. This only takes a moment.
              </p>
              <p className="text-white/40 text-xs">
                Current status: <span data-testid="text-subscription-status" className="font-mono">{status || "pending"}</span>
              </p>
              {secondsLeft <= 0 && (
                <Button
                  variant="outline"
                  onClick={() => setLocation("/billing/select-plan")}
                  data-testid="button-back-to-plans"
                  className="border-white/20 text-white hover:bg-white/10"
                >
                  Still pending? Try again
                </Button>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
